import React, { useState, useMemo } from 'react';
import { IioTable } from '../../components/IioTable';
import { StarButton } from '../../components/StarButton';
import { Project, ProjectStatus, isAdmin } from '../../types/project';
import { CreditCardModal } from '../../components/modals/CreditCardModal';
import { capitalize } from '../../utilities/format';
import { formatLocaleDate } from '../../utilities/date';
import { SortableTh } from '../../components/SortableTh';
import { SingleSelectComboBox } from '../../components/SingleSelectComboBox';
import { compareBy, type SortDir } from '../../utilities/sort';
import { getProjectListOrgFilter, setProjectListOrgFilter } from '../../services/angular-bridge';

const STATUS_ROW_CLASS: Partial<Record<ProjectStatus, string>> = {
  trial: 'info',
  hibernate: 'warning',
  purged: 'danger',
};

type SortField = 'name' | 'companyFirmName' | 'createdAt';

const ALL_ORGS = '__all__';
const NO_ORG = '__none__';
const NAME_ORG_PREFIX = '__name__:';

interface OrgOption {
  id: string;
  name: string;
}

// companyFirmName is the organization name; fall back to the raw id if it's missing.
const orgLabel = (p: Project) => p.companyFirmName || p.organizationId || '';

function ProjectNameCell({ project }: { project: Project }) {
  const active = project.status === 'active' || project.status === 'trial';
  const hibPurged = project.status === 'hibernate' || project.status === 'purged';

  let link: React.ReactNode;
  if (active) {
    const href = project.numCollections > 0
      ? `#!/project/${project.id}`
      : `#!/project/${project.id}/getstarted`;
    link = <a href={href} data-testid="project-list-name-link">{project.name}</a>;
  } else if (hibPurged) {
    link = isAdmin(project)
      ? <a href={`#!/project/${project.id}/admin`} data-testid="project-list-name-link">{project.name}</a>
      : <span>{project.name}</span>;
  } else {
    link = <a href={`#!/project/${project.id}`} data-testid="project-list-name-link">{project.name}</a>;
  }

  return (
    <>
      {link}
      {project.description && <><br /><small data-testid="project-list-description-item">{project.description}</small></>}
    </>
  );
}

function StatusCell({ project }: { project: Project }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  return (
    <>
      {capitalize(project.status)}
      {project.status === 'hibernate' && project.hibernateDate && (
        <small className="text-muted"> {formatLocaleDate(project.hibernateDate)}</small>
      )}
      {project.status === 'trial' && (
        <> <button className="btn btn-xs btn-primary" data-testid="project-list-upgrade-btn" onClick={() => setShowUpgrade(true)}>
          Upgrade <i className="fas fa-arrow-circle-right" aria-hidden="true" />
        </button></>
      )}
      {showUpgrade && <CreditCardModal projectId={project.id} onClose={() => setShowUpgrade(false)} />}
    </>
  );
}

function ProjectRoles({ project }: { project: Project }) {
  if (!project.roles?.length) return null;
  return (
    <>
      {project.roles.map((role, i) =>
        role.permissions?.includes('ADMIN_PROJECT')
          ? <a key={i} className="btn btn-default btn-xs" href={`#!/project/${project.id}/admin`} data-testid="project-list-admin-link">
              <i className="fas fa-cog" aria-hidden="true" /> Project Admin
            </a>
          : <span key={i} className="label label-default">{role.name}</span>
      )}
    </>
  );
}

export interface ProjectTableProps {
  projects: Project[];
  hideCompany?: boolean;
  hideRoles?: boolean;
}

// One option per distinct organizationId, labelled from that group's companyFirmName, plus
// sentinels. Empty when there's only one bucket to choose from — a single-choice dropdown is noise.
function computeOrgOptions(projects: Project[]): OrgOption[] {
  const orgsById = new Map<string, string>();
  // Legacy (non-AccessID) projects have no organizationId — group those by companyFirmName instead.
  const orgsByName = new Map<string, string>();
  let hasNoOrg = false;
  for (const p of projects) {
    if (p.organizationId) {
      if (!orgsById.has(p.organizationId)) orgsById.set(p.organizationId, orgLabel(p));
    } else if (p.companyFirmName) {
      if (!orgsByName.has(p.companyFirmName)) orgsByName.set(p.companyFirmName, NAME_ORG_PREFIX + p.companyFirmName);
    } else {
      hasNoOrg = true;
    }
  }
  if (orgsById.size + orgsByName.size + (hasNoOrg ? 1 : 0) < 2) return [];
  return [
    { id: ALL_ORGS, name: 'All Organizations' },
    ...(hasNoOrg ? [{ id: NO_ORG, name: 'No Organization' }] : []),
    ...[...orgsById].map(([id, name]) => ({ id, name })),
    ...[...orgsByName].map(([name, id]) => ({ id, name })),
  ];
}

export function ProjectTable({ projects, hideCompany = false, hideRoles = false }: ProjectTableProps) {
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Lazily seeded from the persisted preference so the first render already reflects it —
  // restoring it in an effect after mount let the unfiltered list flash before the filter applied.
  const [orgFilter, setOrgFilter] = useState<OrgOption | null>(() => {
    const storedId = getProjectListOrgFilter();
    if (!storedId) return null;
    return computeOrgOptions(projects).find(o => o.id === storedId) ?? null;
  });
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: 'createdAt',
    dir: 'desc',
  });

  const statuses = useMemo(() => [...new Set(projects.map(p => p.status))], [projects]);

  const orgOptions = useMemo<OrgOption[]>(() => computeOrgOptions(projects), [projects]);

  const showOrgFilter = !hideCompany && orgOptions.length > 0;
  const colCount = 5 + (hideCompany ? 0 : 1) + (hideRoles ? 0 : 1);

  const handleOrgFilterChange = (option: OrgOption | null) => {
    setOrgFilter(option);
    setProjectListOrgFilter(option?.id ?? null);
  };

  const filtered = useMemo(() => {
    let result = projects;
    if (nameFilter) {
      const q = nameFilter.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) || (p.companyFirmName ?? '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) result = result.filter(p => p.status === statusFilter);
    // Match on organizationId, not the label — two orgs can share a companyFirmName.
    // Legacy (non-AccessID) projects have no organizationId, so those are matched by
    // companyFirmName instead, via the synthetic NAME_ORG_PREFIX option id.
    if (orgFilter && orgFilter.id !== ALL_ORGS) {
      if (orgFilter.id === NO_ORG) {
        result = result.filter(p => p.organizationId == null && !p.companyFirmName);
      } else if (orgFilter.id.startsWith(NAME_ORG_PREFIX)) {
        const firmName = orgFilter.id.slice(NAME_ORG_PREFIX.length);
        result = result.filter(p => p.organizationId == null && p.companyFirmName === firmName);
      } else {
        result = result.filter(p => p.organizationId === orgFilter.id);
      }
    }
    return [...result].sort(compareBy(
      p => (sort.field === 'companyFirmName' ? orgLabel(p) : p[sort.field] ?? ''),
      sort.dir,
    ));
  }, [projects, nameFilter, statusFilter, orgFilter, sort]);

  const toggleSort = (field: SortField) =>
    setSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));

  if (projects.length === 0) return <div className="alert">No Projects Found</div>;

  return (
    <IioTable data-testid="project-list-table">
      <thead>
        <tr>
          <th>&nbsp;</th>
          <SortableTh label="Name" sortKey="name" current={sort.field} dir={sort.dir} onSort={toggleSort} />
          {!hideCompany && <SortableTh label="Company/Firm" sortKey="companyFirmName" current={sort.field} dir={sort.dir} onSort={toggleSort} />}
          <SortableTh label="Created" sortKey="createdAt" current={sort.field} dir={sort.dir} onSort={toggleSort} />
          <th>Collections</th>
          <th>Status</th>
          {!hideRoles && <th>Role</th>}
        </tr>
      </thead>
      <tbody>
        <tr>
          {/* One cell per header column, in the same order and under the same conditions —
              keep these two rows in lockstep or the filters drift off their columns again. */}
          <th />
          <th>
            <input
              type="search"
              className="input-sm form-control"
              placeholder="Project Filter"
              data-testid="project-list-filter-input"
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
          </th>
          {!hideCompany && (
            <th>
              {showOrgFilter && (
                <SingleSelectComboBox<OrgOption>
                  value={orgFilter}
                  onChange={handleOrgFilterChange}
                  items={orgOptions}
                  getKey={o => o.id}
                  getLabel={o => o.name}
                  placeholder="All Organizations"
                  testId="project-list-org-select"
                />
              )}
            </th>
          )}
          <th />
          <th />
          <th>
            <select
              className="form-control input-sm"
              data-testid="project-list-status-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              {statuses.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
            </select>
          </th>
          {!hideRoles && <th />}
        </tr>
        {filtered.map(project => (
          <tr key={project.id} className={STATUS_ROW_CLASS[project.status] ?? ''} data-testid="project-list-row">
            <td><StarButton project={project} testId="project-list-favorite-btn" /></td>
            <td className="projectLink"><ProjectNameCell project={project} /></td>
            {!hideCompany && <td>{orgLabel(project)}</td>}
            <td>{formatLocaleDate(project.createdAt)}</td>
            <td>{project.numCollections}</td>
            <td><StatusCell project={project} /></td>
            {!hideRoles && <td><ProjectRoles project={project} /></td>}
          </tr>
        ))}
        {filtered.length === 0 && (
          <tr>
            <td colSpan={colCount} className="text-muted">No projects match your filters</td>
          </tr>
        )}
      </tbody>
    </IioTable>
  );
}
